<template>
    <main-dialog
        v-model="isVisible"
        :title="formatFileName(file.name)"
        size="xl"
        @hidden="handleClose"
        :hide-footer="true"
        modal-class="file-preview-modal"
    >
        <div class="preview-container">
            <!-- Image Preview -->
            <div v-if="fileType === 'image'" class="preview-content image-preview">
                <img :src="fileUrl" :alt="file.name" @load="handleLoad" @error="handleError" />
                <div v-if="loading" class="preview-loading">
                    <b-spinner variant="primary" />
                    <p>{{ $t('loadingPreview') }}</p>
                </div>
                <div v-if="error" class="preview-error">
                    <i class="fa fa-exclamation-triangle"></i>
                    <p>{{ $t('previewLoadError') }}</p>
                </div>
            </div>

            <!-- PDF Preview -->
            <div v-else-if="fileType === 'pdf'" class="preview-content pdf-preview">
                <iframe
                    :src="fileUrl"
                    frameborder="0"
                    @load="handleLoad"
                    @error="handleError"
                ></iframe>
                <div v-if="loading" class="preview-loading">
                    <b-spinner variant="primary" />
                    <p>{{ $t('loadingPreview') }}</p>
                </div>
                <div v-if="error" class="preview-error">
                    <i class="fa fa-exclamation-triangle"></i>
                    <p>{{ $t('previewLoadError') }}</p>
                </div>
            </div>

            <!-- HTML Preview -->
            <div v-else-if="fileType === 'html'" class="preview-content html-preview">
                <iframe
                    :src="fileUrl"
                    frameborder="0"
                    sandbox="allow-same-origin allow-scripts"
                    @load="handleLoad"
                    @error="handleError"
                ></iframe>
                <div v-if="loading" class="preview-loading">
                    <b-spinner variant="primary" />
                    <p>{{ $t('loadingPreview') }}</p>
                </div>
                <div v-if="error" class="preview-error">
                    <i class="fa fa-exclamation-triangle"></i>
                    <p>{{ $t('previewLoadError') }}</p>
                </div>
            </div>

            <!-- Text Preview -->
            <div v-else-if="fileType === 'text'" class="preview-content text-preview">
                <div v-if="loading" class="preview-loading">
                    <b-spinner variant="primary" />
                    <p>{{ $t('loadingPreview') }}</p>
                </div>
                <div v-else-if="error" class="preview-error">
                    <i class="fa fa-exclamation-triangle"></i>
                    <p>{{ $t('previewLoadError') }}</p>
                </div>
                <pre v-else class="text-content">{{ textContent }}</pre>
            </div>

            <!-- Unsupported File Type -->
            <div v-else class="preview-content preview-unsupported">
                <i class="fa fa-file-o"></i>
                <p>{{ $t('previewNotSupported') }}</p>
                <b-button variant="primary" @click="openInNewTab">
                    {{ $t('openInNewTab') }}
                </b-button>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="preview-actions">
            <b-button variant="secondary" @click="handleClose">
                <i class="fa fa-times"></i> {{ $t('close') }}
            </b-button>
            <b-button variant="primary" @click="openInNewTab">
                <i class="fa fa-external-link"></i> {{ $t('openInNewTab') }}
            </b-button>
            <b-button variant="success" @click="downloadFile">
                <i class="fa fa-download"></i> {{ $t('download') }}
            </b-button>
        </div>
    </main-dialog>
</template>

<style scoped lang="scss">
.preview-container {
    position: relative;
    min-height: 400px;
    max-height: 70vh;
}

.preview-content {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.image-preview {
    img {
        max-width: 100%;
        max-height: 70vh;
        object-fit: contain;
        display: block;
        margin: 0 auto;
    }
}

.pdf-preview,
.html-preview {
    iframe {
        width: 100%;
        height: 70vh;
        border: 1px solid #ddd;
        border-radius: 4px;
    }
}

.preview-loading,
.preview-error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    
    i {
        font-size: 48px;
        margin-bottom: 16px;
        color: #6c757d;
    }
    
    p {
        margin: 8px 0 0 0;
        color: #6c757d;
    }
}

.preview-error {
    i {
        color: #dc3545;
    }
    
    p {
        color: #dc3545;
    }
}

.text-preview {
    display: block;

    .text-content {
        width: 100%;
        max-height: 70vh;
        overflow: auto;
        padding: 20px;
        margin: 0;
        background: #1e1e1e;
        color: #d4d4d4;
        border-radius: 4px;
        border: 1px solid #333;
        font-family: 'Courier New', Courier, monospace;
        font-size: 14px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-wrap: break-word;
    }
}

.preview-unsupported {
    flex-direction: column;
    padding: 40px;
    
    i {
        font-size: 64px;
        color: #6c757d;
        margin-bottom: 16px;
    }
    
    p {
        margin-bottom: 24px;
        color: #6c757d;
        font-size: 16px;
    }
}

.preview-actions {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 20px;
    border-top: 1px solid #dee2e6;
    
    button {
        i {
            margin-right: 4px;
        }
    }
}
</style>

<style lang="scss">
.file-preview-modal {
    .modal-dialog {
        max-width: 90vw !important;
    }
    
    .modal-body {
        padding: 0;
    }
    
    .preview-container {
        padding: 20px;
    }
    
    .preview-actions {
        padding: 0 20px 20px 20px;
        margin: 0;
    }
}
</style>

<script>
import { truncate } from 'lodash';

export default {
    props: {
        show: {
            type: Boolean,
            required: true
        },
        file: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            loading: true,
            error: false,
            textContent: ''
        };
    },

    computed: {
        isVisible: {
            get() {
                return this.show;
            },
            set(value) {
                if (!value) {
                    this.$emit('close');
                }
            }
        },

        fileUrl() {
            return this.file.local_url;
        },

        fileType() {
            const extension = this.getFileExtension(this.file.name).toLowerCase();
            
            // Image types
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
                return 'image';
            }
            
            // PDF
            if (extension === 'pdf') {
                return 'pdf';
            }
            
            // HTML
            if (['html', 'htm'].includes(extension)) {
                return 'html';
            }

            // Text
            if (['txt', 'log', 'csv', 'md'].includes(extension)) {
                return 'text';
            }

            return 'unsupported';
        }
    },

    watch: {
        show(newVal) {
            if (newVal) {
                this.loading = true;
                this.error = false;
                this.textContent = '';

                if (this.fileType === 'text') {
                    this.fetchTextContent();
                }
            }
        }
    },

    methods: {
        getFileExtension(name) {
            const parts = name.split('.');
            return parts.length > 1 ? parts[parts.length - 1] : '';
        },

        formatFileName(name) {
            return truncate(name, {
                omission: '...',
                length: 50
            });
        },

        handleLoad() {
            this.loading = false;
            this.error = false;
        },

        handleError() {
            this.loading = false;
            this.error = true;
        },

        handleClose() {
            this.$emit('close');
        },

        openInNewTab() {
            window.open(this.fileUrl, '_blank');
        },

        fetchTextContent() {
            this.$mediaHttp.get(this.fileUrl, { responseType: 'text' })
                .then(resp => {
                    this.textContent = resp.data;
                    this.loading = false;
                })
                .catch(() => {
                    this.error = true;
                    this.loading = false;
                });
        },

        downloadFile() {
            this.$emit('download', this.file);
        }
    }
};
</script>
